export interface RetryOptions {
  limit?: number;
  retryDelay?: number;
  exponentialBackoff?: boolean;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  onRetry?: (error: unknown, attempt: number) => void;
  /** Custom function to calculate retry delay based on error and attempt */
  getRetryDelay?: (
    error: unknown,
    attempt: number,
    defaultDelay: number,
  ) => number;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function retry<T>(
  fn: () => T | Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const {
    limit = 3,
    retryDelay = 1000,
    exponentialBackoff = false,
    shouldRetry = () => true,
    onRetry,
    getRetryDelay,
  } = options;

  let lastError: unknown;
  let attempt = 0;

  while (attempt <= limit) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      attempt++;

      if (!shouldRetry(error, attempt)) {
        throw error;
      }

      if (onRetry) {
        onRetry(error, attempt);
      }

      // Calculate delay
      let delay = retryDelay;
      if (exponentialBackoff) {
        delay = retryDelay * Math.pow(2, attempt - 1);
      }

      // Use custom delay calculator if provided
      if (getRetryDelay) {
        delay = getRetryDelay(error, attempt, delay);
      }

      // Wait before retrying
      await sleep(delay);
    }
  }

  throw lastError;
}
